/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2023, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { AeroDialogService } from '../../aero/aero-dialog/aero-dialog.service';
import { TranslationService } from '../../common/translation.service';
import { AnalyticsService } from '../../common/analytics.service';
// eslint-disable-next-line import/no-cycle
import { AddStudentDialogComponent } from './add-student-dialog/add-student-dialog.component';

/**
 * Angular service that handles Account Settings Dialog.
 */

@Injectable({
  providedIn: 'root',
})
export class AccountSettingsDialogService {

  /**
   * Holds value of student code.
   */
  public studentCode: string;

  /**
   * Username of the student.
   */
  public username: string;

  /**
   * Subject to listen change to perform component method.
   */
  public serviceSubject = new Subject();

  /**
   * Subject to listen action on success.
   */
  public settingResponse = new Subject();

  /**
   * Id of account.
   */
  accountId: string;

  /**
   * Constructor.
   *
   * @param AeroDialog Aero Dialog Service
   * @param analyticsService Angular Alalytical Service
   * @param $translate Angularjs Translation service
   * @param http Http client.
   * @param bulbErrorService Error Service
   */
  constructor(
    private AeroDialog: AeroDialogService,
    private analyticsService: AnalyticsService,
    private dynamicDialogRef: DynamicDialogRef,
    private $translate: TranslationService,
    private http: HttpClient,
    @Inject('BulbErrorService') private bulbErrorService,
  ) {
  }

  /**
   * Show add child modal.
   *
   * @param accountId ID of account.
   */
  showAddChildDialog(accountId: string, onSuccess?: (student: any) => void) {
    this.accountId = accountId;
    this.AeroDialog.showDialog({
      svgIconName: 'circle-plus-solid',
      iconClass: 'aero-highlight',
      styleClass: 'aero-bulb-plus-icon',
      customContentComponent: AddStudentDialogComponent,
      title: `${this.$translate.translateInstant('account_settings_add_student')}`,
      detail: `${this.$translate.translateInstant('account_settings_connect_student_info')}`,
      primaryLabel: this.$translate.translateInstant('account_settings_add_student'),
      closeOnPrimary: false,
      showSecondary: false,
      width: '50%',
      onPrimary: () => {
        this.addStudent().then((newStudent) => {
          this.closeDialog();
          if (onSuccess) {
            onSuccess(newStudent);
          }
        }).catch(() => {
          // Modal stays open because of close: false in your error subject
        });
      },
      data: {
        valueChange: (value) => {
          this.username = value.username;
          this.studentCode = value.studentCode;
        },
      },
    });
  }

  /**
   * Will close the dialog.
   */
  closeDialog() {
    document.getElementsByTagName('p-dynamicdialog')[0].remove();
    this.dynamicDialogRef.close();
  }

  /**
   * Call api to add child into parent.
   */
  addStudent() {
    return this.http.patch(
      `accounts/${this.accountId}/familyMembers/addChild`,
      {
        username: this.username,
        inviteCode: this.studentCode.toUpperCase(),
      },
    )
      .toPromise()
      .then((response: any) => {
        this.serviceSubject.next({ close: true });
        const addStudentEventObject = {
          event: 'FamilyAccountAddStudent',
          studentId: response.id,
          studentUsername: response.username,
          userId: this.accountId,
        };
        this.analyticsService.pushEvent(addStudentEventObject);
        this.settingResponse.next({ type: 'AddChildModal' });

        return response;
      })
      .catch((error) => {
        this.serviceSubject.next(
          {
            close: false,
            error: true,
          },
        );
        throw error;
      });
  }
}
